import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  usePaxStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import type { CloseHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { usePeekPriceStore } from '@apps/main/new-desktop/modules/peekPriceModal';

import {
  trackPaxStartChanging,
  trackPaxEndChanging,
} from '@libs/google-analytics-events';
import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
} from '@libs/trip-planner-interfaces';

import { HotelSelectionModalType } from '../models/hotelSelectionModals';

import { useHotelSelectionDestinationSelectorStore } from './destinationSelector/destinationSelectorStore';
import { useHotelSelectionStore } from './hotelSelection/hotelSelectionStore';

export const useHotelSelectionModalsStore = defineStore(
  'HotelSelectionModalsStore',
  () => {
    const mapStore = useMapStore();
    const paxStore = usePaxStore();
    const hotelSelectionStore = useHotelSelectionStore();
    const destinationSelectorStore =
      useHotelSelectionDestinationSelectorStore();
    const statsigEventsStore = useStatsigEventsStore();
    const peekPriceStore = usePeekPriceStore();

    const { updateMapShouldCenterOnMarkers } = mapStore;
    // State
    const hotelSelectionModalType = ref<HotelSelectionModalType>(
      HotelSelectionModalType.None
    );

    // Actions
    function closeHotelSelectionModal(
      origin?: CloseHotelSelectionFiltersOrigin
    ): void {
      hotelSelectionModalType.value = HotelSelectionModalType.None;
      if (origin) {
        hotelSelectionStore.handleCloseFiltersEvent(origin);
      }
    }

    function openPaxSelectorModal(): void {
      trackPaxStartChanging();
      statsigEventsStore.logCorePaxModalOpened({
        source: CoreEventSource.HotelSelection,
      });
      hotelSelectionModalType.value = HotelSelectionModalType.PaxSelector;
    }

    function confirmPaxSelectorModal(newPax: Pax): void {
      trackPaxEndChanging();
      paxStore.updatePax(newPax, CoreEventSource.HotelSelection);
      hotelSelectionModalType.value = HotelSelectionModalType.None;
    }

    function openDestinationSelectorModal(
      searchText: string,
      ingredientId: IngredientId | undefined
    ): void {
      destinationSelectorStore.setDestinationSelectorData(
        searchText,
        ingredientId
      );
      hotelSelectionModalType.value =
        HotelSelectionModalType.DestinationSelector;
    }

    function confirmDestinationSelectorModal(
      newDestination: AutoCompleteSuggestion | null | undefined
    ): void {
      if (newDestination) {
        updateMapShouldCenterOnMarkers(true);
        destinationSelectorStore.handleConfirmSelection(newDestination);
      }
      closeHotelSelectionModal();
    }

    function openFiltersModal(): void {
      hotelSelectionModalType.value =
        HotelSelectionModalType.MobileFiltersModal;
    }

    function openWhyWeSuggestModal(): void {
      hotelSelectionModalType.value = HotelSelectionModalType.WhyWeSuggest;
    }

    function openSemboBenefitsModal(): void {
      hotelSelectionModalType.value = HotelSelectionModalType.SemboBenefits;
    }

    function openPeekPriceModal(
      ingredientIndex: number,
      hotelCode: string
    ): void {
      peekPriceStore.loadPrices(ingredientIndex, hotelCode);
      hotelSelectionModalType.value = HotelSelectionModalType.PeekPrice;
    }

    function closePeekPriceModal(): void {
      hotelSelectionModalType.value = HotelSelectionModalType.None;
      peekPriceStore.clean();
    }

    function confirmPeekPriceModal(): void {
      hotelSelectionModalType.value = HotelSelectionModalType.None;
      peekPriceStore.confirm();
    }

    return {
      // State
      hotelSelectionModalType,

      // Actions
      closeHotelSelectionModal,
      openPaxSelectorModal,
      confirmPaxSelectorModal,
      openDestinationSelectorModal,
      confirmDestinationSelectorModal,
      openFiltersModal,
      openWhyWeSuggestModal,
      openSemboBenefitsModal,
      openPeekPriceModal,
      closePeekPriceModal,
      confirmPeekPriceModal,
    };
  }
);
